import { type MatrixClient, type SecretStorage } from "matrix-js-sdk/src/matrix";

import { decodeRecoveryKey, type CryptoCallbacks, type GeneratedSecretStorageKey } from "matrix-js-sdk/src/crypto-api";

export const SIMPLII_MATRIX_RECOVERY_GET = "SIMPLII_MATRIX_RECOVERY_GET";

export const SIMPLII_MATRIX_RECOVERY_VALUE = "SIMPLII_MATRIX_RECOVERY_VALUE";

export const SIMPLII_MATRIX_RECOVERY_SAVE = "SIMPLII_MATRIX_RECOVERY_SAVE";

type RecoveryStatus = "pending" | "active" | "stale" | "revoked" | null;

type RecoveryResponse = {
    type?: string;
    requestId?: string;
    matrixUserId?: string;

    recoveryKey?: string | null;
    secretStorageKeyId?: string | null;
    keyBackupVersion?: string | null;
    status?: RecoveryStatus;
};

type StoredRecoveryRecord = {
    recoveryKey: string | null;
    secretStorageKeyId: string | null;
    keyBackupVersion: string | null;
    status: RecoveryStatus;
};

type SaveRecoveryOptions = {
    status: "pending" | "active";

    secretStorageKeyId?: string;
    keyBackupVersion?: string;
    deviceId?: string;
    replaceExisting?: boolean;
};

type SecretStoragePrivateKey = Uint8Array<ArrayBuffer>;

type AccountCryptoState = {
    defaultSecretStorageKeyId: string | null;
    hasCrossSigningKeys: boolean;
    keyBackupInfo: unknown | null;
    hasAnyEncryptionSetup: boolean;
};

type DeviceVerificationLike = {
    crossSigningVerified?: boolean;
    localVerified?: boolean;
    signedByOwner?: boolean;
    isVerified?: () => boolean;
};

const RECOVERY_RESPONSE_TIMEOUT_MS = 10_000;

const MANAGED_SECRET_STORAGE_NAMES = [
    "m.cross_signing.master",
    "m.cross_signing.self_signing",
    "m.cross_signing.user_signing",
    "m.megolm_backup.v1",
] as const;

/**
 * Encoded Matrix recovery keys fetched from Brain.
 *
 * Key:
 *     Matrix user ID
 */
const encodedRecoveryKeyCache = new Map<string, string>();

/**
 * Decoded Secret Storage private keys.
 *
 * Key:
 *     Matrix user ID | Secret Storage key ID
 */
const secretStorageKeyCache = new Map<string, SecretStoragePrivateKey>();

/**
 * Default Secret Storage key ID for each Matrix user.
 */
const defaultSecretStorageKeyIdByUser = new Map<string, string>();

/**
 * The Secret Storage key ID which was actually validated against
 * the recovery key stored by Brain.
 */
const matchedSecretStorageKeyIdByUser = new Map<string, string>();

/**
 * Matrix clients registered after Rust crypto initialisation.
 *
 * createSimpliiManagedCryptoCallbacks() is installed before the client
 * is started, so the callback resolves the active client from this map.
 */
const matrixClientByUser = new Map<string, MatrixClient>();

/**
 * Recovery runs currently in progress.
 *
 * Key:
 *     Matrix user ID | Matrix device ID
 */
const recoveryInFlight = new Map<string, Promise<void>>();

/**
 * Sessions which have successfully completed managed recovery.
 */
const completedRecoverySessions = new Set<string>();

function createRequestId(): string {
    return ["simplii_recovery", Date.now(), Math.random().toString(36).slice(2)].join("_");
}

function normalizeRecoveryKeyForComparison(recoveryKey: string): string {
    return recoveryKey.replace(/\s+/g, "").trim();
}

function getTrustedParentOrigin(): string | null {
    try {
        const params = new URLSearchParams(window.location.search);

        const configuredOrigin = params.get("parentOrigin")?.trim();

        if (configuredOrigin && configuredOrigin !== "*") {
            return new URL(configuredOrigin, window.location.href).origin;
        }
    } catch {
        // Fall through to document.referrer.
    }

    try {
        if (document.referrer) {
            return new URL(document.referrer).origin;
        }
    } catch {
        // No trusted parent origin is available.
    }

    return null;
}

function isBrainEmbedded(): boolean {
    try {
        const params = new URLSearchParams(window.location.search);

        return params.get("embedded") === "1" && window.parent !== window;
    } catch {
        return false;
    }
}

function secretStorageCacheId(matrixUserId: string, keyId: string): string {
    return `${matrixUserId}|${keyId}`;
}

function recoverySessionId(matrixUserId: string, deviceId: string): string {
    return `${matrixUserId}|${deviceId}`;
}

function decodeManagedRecoveryKey(recoveryKey: string): SecretStoragePrivateKey {
    const normalized = recoveryKey.trim();

    if (!normalized) {
        throw new Error("The managed Matrix recovery key is empty");
    }

    return decodeRecoveryKey(normalized);
}

function waitForRecoveryResponse(
    requestId: string,
    matrixUserId: string,
    expectedOrigin: string,
): Promise<RecoveryResponse> {
    return new Promise((resolve) => {
        const cleanup = (): void => {
            window.clearTimeout(timer);

            window.removeEventListener("message", onMessage);
        };

        const onMessage = (event: MessageEvent): void => {
            if (event.source !== window.parent) {
                return;
            }

            if (event.origin !== expectedOrigin) {
                return;
            }

            const data = event.data as RecoveryResponse;

            if (!data || data.type !== SIMPLII_MATRIX_RECOVERY_VALUE) {
                return;
            }

            if (data.requestId !== requestId) {
                return;
            }

            /*
             * Accept missing matrixUserId for compatibility with
             * older Brain builds, but reject an explicit mismatch.
             */
            if (data.matrixUserId && data.matrixUserId !== matrixUserId) {
                return;
            }

            cleanup();
            resolve(data);
        };

        const timer = window.setTimeout(() => {
            cleanup();

            resolve({
                requestId,
                matrixUserId,
                recoveryKey: null,
            });
        }, RECOVERY_RESPONSE_TIMEOUT_MS);

        window.addEventListener("message", onMessage);
    });
}

async function requestRecoveryValueFromParent(
    matrixUserId: string,
    payload: Record<string, unknown>,
): Promise<RecoveryResponse> {
    if (!isBrainEmbedded()) {
        return {
            matrixUserId,
            recoveryKey: null,
        };
    }

    const expectedOrigin = getTrustedParentOrigin();

    if (!expectedOrigin) {
        throw new Error("Simplii managed recovery requires a trusted parentOrigin");
    }

    const requestId = typeof payload.requestId === "string" ? payload.requestId : createRequestId();

    const responsePromise = waitForRecoveryResponse(requestId, matrixUserId, expectedOrigin);

    window.parent.postMessage(
        {
            ...payload,
            requestId,
            matrixUserId,
        },
        expectedOrigin,
    );

    return responsePromise;
}

async function getStoredRecoveryRecord(matrixUserId: string): Promise<StoredRecoveryRecord> {
    const empty: StoredRecoveryRecord = {
        recoveryKey: null,
        secretStorageKeyId: null,
        keyBackupVersion: null,
        status: null,
    };

    if (!isBrainEmbedded()) {
        return empty;
    }

    const cached = encodedRecoveryKeyCache.get(matrixUserId);

    if (cached) {
        return {
            ...empty,
            recoveryKey: cached,
            status: "active",
        };
    }

    const response = await requestRecoveryValueFromParent(matrixUserId, {
        type: SIMPLII_MATRIX_RECOVERY_GET,

        requestId: createRequestId(),
    });

    const recoveryKey = response.recoveryKey?.trim() || null;

    const status = response.status ?? null;

    /*
     * Only an active recovery record may unlock an existing
     * Matrix encryption identity.
     */
    if (recoveryKey && status === "active") {
        encodedRecoveryKeyCache.set(matrixUserId, recoveryKey);
    }

    return {
        recoveryKey: status === "active" ? recoveryKey : null,

        secretStorageKeyId: response.secretStorageKeyId?.trim() || null,

        keyBackupVersion: response.keyBackupVersion?.trim() || null,

        status,
    };
}

async function saveStoredRecoveryKey(
    matrixUserId: string,
    recoveryKey: string,
    options: SaveRecoveryOptions,
): Promise<boolean> {
    if (!isBrainEmbedded()) {
        return false;
    }

    const response = await requestRecoveryValueFromParent(matrixUserId, {
        type: SIMPLII_MATRIX_RECOVERY_SAVE,

        requestId: createRequestId(),

        matrixUserId,
        recoveryKey,

        status: options.status,

        secretStorageKeyId: options.secretStorageKeyId,

        keyBackupVersion: options.keyBackupVersion,

        deviceId: options.deviceId,

        replaceExisting: options.replaceExisting === true,
    });

    const returnedKey = response.recoveryKey?.trim() || "";

    const saved =
        Boolean(returnedKey) &&
        normalizeRecoveryKeyForComparison(returnedKey) === normalizeRecoveryKeyForComparison(recoveryKey);

    /*
     * Only cache a validated active key. A pending key must not
     * be treated as usable account recovery.
     */
    if (saved && options.status === "active") {
        encodedRecoveryKeyCache.set(matrixUserId, recoveryKey);
    }

    return saved;
}

function orderedRequestedKeyIds(matrixUserId: string, keys: Record<string, unknown>): string[] {
    const availableKeyIds = Object.keys(keys);

    const ordered: string[] = [];

    const matchedKeyId = matchedSecretStorageKeyIdByUser.get(matrixUserId);

    const defaultKeyId = defaultSecretStorageKeyIdByUser.get(matrixUserId);

    if (matchedKeyId && availableKeyIds.includes(matchedKeyId)) {
        ordered.push(matchedKeyId);
    }

    if (defaultKeyId && availableKeyIds.includes(defaultKeyId) && !ordered.includes(defaultKeyId)) {
        ordered.push(defaultKeyId);
    }

    for (const keyId of availableKeyIds) {
        if (!ordered.includes(keyId)) {
            ordered.push(keyId);
        }
    }

    return ordered;
}

function getCachedRequestedKey(
    matrixUserId: string,
    keys: Record<string, unknown>,
): [string, SecretStoragePrivateKey] | null {
    const requestedKeyIds = orderedRequestedKeyIds(matrixUserId, keys);

    for (const keyId of requestedKeyIds) {
        const cachedKey = secretStorageKeyCache.get(secretStorageCacheId(matrixUserId, keyId));

        if (cachedKey) {
            return [keyId, cachedKey];
        }
    }

    return null;
}

async function findMatchingKeyDescription(
    cli: MatrixClient,
    matrixUserId: string,
    privateKey: SecretStoragePrivateKey,
    keyDescriptions: Record<string, SecretStorage.SecretStorageKeyDescription>,
): Promise<string | null> {
    const orderedKeyIds = orderedRequestedKeyIds(matrixUserId, keyDescriptions);

    for (const keyId of orderedKeyIds) {
        const keyInfo = keyDescriptions[keyId];

        if (!keyInfo) {
            continue;
        }

        try {
            const valid = await cli.secretStorage.checkKey(
                privateKey,
                keyInfo as SecretStorage.SecretStorageKeyDescriptionAesV1,
            );

            if (!valid) {
                continue;
            }

            secretStorageKeyCache.set(secretStorageCacheId(matrixUserId, keyId), privateKey);

            matchedSecretStorageKeyIdByUser.set(matrixUserId, keyId);

            return keyId;
        } catch (error) {
            console.warn("[Simplii Chat] Failed checking a Secret Storage key description", {
                matrixUserId,
                keyId,
                error,
            });
        }
    }

    return null;
}

export function createSimpliiManagedCryptoCallbacks(
    matrixUserId: string,
    existingCallbacks: CryptoCallbacks = {},
): CryptoCallbacks {
    const callbacks: CryptoCallbacks = {
        ...existingCallbacks,

        getSecretStorageKey: async (opts, name) => {
            const cached = getCachedRequestedKey(matrixUserId, opts.keys);

            if (cached) {
                console.info("[Simplii Chat] Using cached managed Secret Storage key", {
                    matrixUserId,
                    keyId: cached[0],
                    secretName: name,
                });

                return cached;
            }

            const recovery = await getStoredRecoveryRecord(matrixUserId);

            const recoveryKey = recovery.recoveryKey;

            if (!recoveryKey) {
                console.warn("[Simplii Chat] No Simplii-managed recovery key is available", {
                    matrixUserId,
                    secretName: name,
                    requestedKeyIds: Object.keys(opts.keys),
                });

                /*
                 * Outside Brain, retain Element's standard manual
                 * recovery-key dialog.
                 */
                if (!isBrainEmbedded() && existingCallbacks.getSecretStorageKey) {
                    return existingCallbacks.getSecretStorageKey(opts, name);
                }

                return null;
            }

            let privateKey: SecretStoragePrivateKey;

            try {
                privateKey = decodeManagedRecoveryKey(recoveryKey);
            } catch (error) {
                console.error("[Simplii Chat] Failed to decode the managed Matrix recovery key", error);

                if (!isBrainEmbedded() && existingCallbacks.getSecretStorageKey) {
                    return existingCallbacks.getSecretStorageKey(opts, name);
                }

                return null;
            }

            const cli = matrixClientByUser.get(matrixUserId);

            if (!cli) {
                console.warn("[Simplii Chat] Matrix client is not registered for Secret Storage validation", {
                    matrixUserId,
                    secretName: name,
                });

                return null;
            }

            const matchingKeyId = await findMatchingKeyDescription(cli, matrixUserId, privateKey, opts.keys);

            if (!matchingKeyId) {
                console.error("[Simplii Chat] Managed recovery key did not match any requested Secret Storage key", {
                    matrixUserId,
                    secretName: name,
                    requestedKeyIds: Object.keys(opts.keys),
                });

                return null;
            }

            encodedRecoveryKeyCache.set(matrixUserId, recoveryKey);

            console.info("[Simplii Chat] Supplied validated managed Secret Storage key", {
                matrixUserId,
                keyId: matchingKeyId,
                secretName: name,
            });

            return [matchingKeyId, privateKey];
        },

        cacheSecretStorageKey: (keyId, keyInfo, key): void => {
            if (keyId && key) {
                secretStorageKeyCache.set(secretStorageCacheId(matrixUserId, keyId), key);

                defaultSecretStorageKeyIdByUser.set(matrixUserId, keyId);
            }

            existingCallbacks.cacheSecretStorageKey?.(keyId, keyInfo, key);
        },
    };

    return callbacks;
}

async function refreshDefaultSecretStorageKeyId(cli: MatrixClient, matrixUserId: string): Promise<string | null> {
    const defaultKeyId = await cli.secretStorage.getDefaultKeyId();

    if (defaultKeyId) {
        defaultSecretStorageKeyIdByUser.set(matrixUserId, defaultKeyId);
    } else {
        defaultSecretStorageKeyIdByUser.delete(matrixUserId);
    }

    return defaultKeyId;
}

async function collectSecretStorageKeyDescriptions(
    cli: MatrixClient,
    matrixUserId: string,
): Promise<Record<string, SecretStorage.SecretStorageKeyDescription>> {
    const descriptions: Record<string, SecretStorage.SecretStorageKeyDescription> = {};

    const defaultKeyTuple = await cli.secretStorage.getKey();

    if (defaultKeyTuple) {
        const [defaultKeyId, defaultKeyInfo] = defaultKeyTuple;

        descriptions[defaultKeyId] = defaultKeyInfo;

        defaultSecretStorageKeyIdByUser.set(matrixUserId, defaultKeyId);
    }

    const isStored = cli.secretStorage.isStored.bind(cli.secretStorage) as (
        secretName: string,
    ) => Promise<Record<string, SecretStorage.SecretStorageKeyDescriptionAesV1> | null>;

    for (const secretName of MANAGED_SECRET_STORAGE_NAMES) {
        try {
            const storedWithKeys = await isStored(secretName);

            if (!storedWithKeys) {
                continue;
            }

            for (const [keyId, keyInfo] of Object.entries(storedWithKeys)) {
                descriptions[keyId] = keyInfo;
            }
        } catch (error) {
            console.warn("[Simplii Chat] Failed reading Secret Storage metadata", {
                matrixUserId,
                secretName,
                error,
            });
        }
    }

    return descriptions;
}

async function validateAndCacheRecoveryKey(
    cli: MatrixClient,
    matrixUserId: string,
    recoveryKey: string,
): Promise<{
    keyId: string;
    privateKey: SecretStoragePrivateKey;
}> {
    const privateKey = decodeManagedRecoveryKey(recoveryKey);

    await refreshDefaultSecretStorageKeyId(cli, matrixUserId);

    const keyDescriptions = await collectSecretStorageKeyDescriptions(cli, matrixUserId);

    if (Object.keys(keyDescriptions).length === 0) {
        throw new Error("The Matrix account has no Secret Storage key descriptions");
    }

    const matchingKeyId = await findMatchingKeyDescription(cli, matrixUserId, privateKey, keyDescriptions);

    if (!matchingKeyId) {
        throw new Error(
            "The Simplii-managed recovery key does not match any Secret Storage key used by this Matrix account",
        );
    }

    secretStorageKeyCache.set(secretStorageCacheId(matrixUserId, matchingKeyId), privateKey);

    matchedSecretStorageKeyIdByUser.set(matrixUserId, matchingKeyId);

    encodedRecoveryKeyCache.set(matrixUserId, recoveryKey);

    console.info("[Simplii Chat] Managed recovery key matched Secret Storage", {
        matrixUserId,
        keyId: matchingKeyId,
    });

    return {
        keyId: matchingKeyId,
        privateKey,
    };
}

async function inspectAccountCryptoState(cli: MatrixClient): Promise<AccountCryptoState> {
    const cryptoApi = cli.getCrypto();

    const matrixUserId = cli.getSafeUserId();

    if (!cryptoApi) {
        throw new Error("Matrix Rust crypto is unavailable");
    }

    let defaultSecretStorageKeyId: string | null = null;

    let hasCrossSigningKeys = false;
    let keyBackupInfo: unknown | null = null;

    try {
        defaultSecretStorageKeyId = await refreshDefaultSecretStorageKeyId(cli, matrixUserId);
    } catch (error) {
        console.warn("[Simplii Chat] Failed checking default Secret Storage key", error);
    }

    try {
        hasCrossSigningKeys = await cryptoApi.userHasCrossSigningKeys(matrixUserId, true);
    } catch (error) {
        console.warn("[Simplii Chat] Failed checking published cross-signing keys", error);
    }

    try {
        keyBackupInfo = await cryptoApi.getKeyBackupInfo();
    } catch (error) {
        console.warn("[Simplii Chat] Failed checking Matrix key backup", error);
    }

    return {
        defaultSecretStorageKeyId,
        hasCrossSigningKeys,
        keyBackupInfo,

        hasAnyEncryptionSetup: Boolean(defaultSecretStorageKeyId || hasCrossSigningKeys || keyBackupInfo),
    };
}

const authUploadDeviceSigningKeys = async (makeRequest: any): Promise<void> => {
    /*
     * Send the first cross-signing upload without an auth
     * dictionary. Synapse's temporary replacement permission
     * allows this request without UIA.
     */
    await makeRequest(null);
};

function isDeviceVerified(status: DeviceVerificationLike | null | undefined): boolean {
    if (!status) {
        return false;
    }

    if (typeof status.isVerified === "function") {
        try {
            if (status.isVerified()) {
                return true;
            }
        } catch {
            // Fall through to individual flags.
        }
    }

    return Boolean(status.crossSigningVerified || status.localVerified);
}

async function bootstrapExistingCrossSigning(cli: MatrixClient): Promise<void> {
    const cryptoApi = cli.getCrypto();

    const matrixUserId = cli.getSafeUserId();

    if (!cryptoApi) {
        throw new Error("Matrix Rust crypto is unavailable");
    }

    cryptoApi.setTrustCrossSignedDevices(true);

    await cryptoApi.bootstrapCrossSigning({
        /*
         * Never reset an existing Matrix identity during automatic
         * managed recovery.
         */
        setupNewCrossSigning: false,

        authUploadDeviceSigningKeys,
    });

    const ready = await cryptoApi.isCrossSigningReady();

    if (!ready) {
        throw new Error("Matrix cross-signing private keys could not be restored from Secret Storage");
    }

    console.info("[Simplii Chat] Existing Matrix cross-signing identity restored", {
        matrixUserId,
    });
}

async function ensureCurrentDeviceCrossSigned(cli: MatrixClient): Promise<void> {
    const cryptoApi = cli.getCrypto();

    const matrixUserId = cli.getSafeUserId();

    const deviceId = cli.getDeviceId();

    if (!cryptoApi || !deviceId) {
        throw new Error("Matrix crypto or current device ID is unavailable");
    }

    try {
        await cryptoApi.getUserDeviceInfo([matrixUserId], true);
    } catch {
        // Continue using the current device cache.
    }

    let status = await cryptoApi.getDeviceVerificationStatus(matrixUserId, deviceId);

    if (isDeviceVerified(status)) {
        console.info("[Simplii Chat] Current Matrix device is already verified", {
            matrixUserId,
            deviceId,
        });

        return;
    }

    /*
     * This publishes a self-signing signature for the current
     * managed Matrix device.
     */
    await cryptoApi.crossSignDevice(deviceId);

    for (let attempt = 0; attempt < 10; attempt += 1) {
        try {
            await cryptoApi.getUserDeviceInfo([matrixUserId], true);
        } catch {
            // Continue with current crypto state.
        }

        const status = await cryptoApi.getDeviceVerificationStatus(matrixUserId, deviceId);

        if (status?.crossSigningVerified || status?.signedByOwner) {
            console.info("[Simplii Chat] Current Matrix device cross-signed and verified", {
                matrixUserId,
                deviceId,
                crossSigningVerified: status.crossSigningVerified,
                signedByOwner: status.signedByOwner,
            });

            return;
        }

        await new Promise<void>((resolve) => {
            window.setTimeout(resolve, 500);
        });
    }

    throw new Error(`Matrix device ${deviceId} was cross-signed, but verification status did not update`);
}

function startFullBackupRestoreInBackground(cli: MatrixClient): void {
    const cryptoApi = cli.getCrypto();

    const matrixUserId = cli.getUserId();

    const deviceId = cli.getDeviceId();

    if (!cryptoApi) {
        return;
    }

    void cryptoApi
        .restoreKeyBackup()
        .then((result) => {
            console.info("[Simplii Chat] Full Matrix room-key backup restore completed", {
                matrixUserId,
                deviceId,
                result,
            });
        })
        .catch((error) => {
            console.warn("[Simplii Chat] Full Matrix room-key backup restore failed", {
                matrixUserId,
                deviceId,
                error,
            });
        });
}

async function restoreBackupFromSecretStorage(cli: MatrixClient): Promise<void> {
    const cryptoApi = cli.getCrypto();

    const matrixUserId = cli.getSafeUserId();

    const deviceId = cli.getDeviceId();

    if (!cryptoApi) {
        throw new Error("Matrix Rust crypto is unavailable");
    }

    const backupInfo = await cryptoApi.getKeyBackupInfo();

    if (!backupInfo) {
        console.warn("[Simplii Chat] No Matrix key backup exists for this account", {
            matrixUserId,
            deviceId,
        });

        return;
    }

    /*
     * Loads and validates the backup private key from Secret
     * Storage and stores it in the local Rust crypto store.
     *
     * This also enables automatic lazy recovery of individual
     * missing Megolm sessions.
     */
    await cryptoApi.loadSessionBackupPrivateKeyFromSecretStorage();

    const storedBackupPrivateKey = await cryptoApi.getSessionBackupPrivateKey();

    if (!storedBackupPrivateKey) {
        throw new Error("Matrix backup private key was not cached after loading it from Secret Storage");
    }

    const backupCheck = await cryptoApi.checkKeyBackupAndEnable();

    if (!backupCheck) {
        throw new Error("Matrix key backup exists but could not be enabled");
    }

    console.info("[Simplii Chat] Matrix backup private key loaded and automatic recovery enabled", {
        matrixUserId,
        deviceId,
    });

    /*
     * A full backup can be large, so do not block application
     * startup while every room key is downloaded.
     */
    startFullBackupRestoreInBackground(cli);
}

async function tryRecoverUsingLocalCrypto(cli: MatrixClient): Promise<boolean> {
    const cryptoApi = cli.getCrypto();

    const matrixUserId = cli.getSafeUserId();

    const deviceId = cli.getDeviceId();

    if (!cryptoApi || !deviceId) {
        return false;
    }

    try {
        const crossSigningReady = await cryptoApi.isCrossSigningReady();

        if (!crossSigningReady) {
            console.info("[Simplii Chat] Cross-signing keys are not cached locally", {
                matrixUserId,
                deviceId,
            });

            return false;
        }

        await ensureCurrentDeviceCrossSigned(cli);

        const backupInfo = await cryptoApi.getKeyBackupInfo();

        if (!backupInfo) {
            return true;
        }

        const localBackupPrivateKey = await cryptoApi.getSessionBackupPrivateKey();

        if (!localBackupPrivateKey) {
            console.info(
                "[Simplii Chat] Cross-signing is available locally, but the backup key requires managed recovery",
                {
                    matrixUserId,
                    deviceId,
                },
            );

            return false;
        }

        const backupCheck = await cryptoApi.checkKeyBackupAndEnable();

        if (!backupCheck) {
            return false;
        }

        console.info("[Simplii Chat] Using locally cached Matrix cross-signing and backup keys", {
            matrixUserId,
            deviceId,
        });

        startFullBackupRestoreInBackground(cli);

        return true;
    } catch (error) {
        console.info("[Simplii Chat] Local crypto recovery was not sufficient; using managed recovery", {
            matrixUserId,
            deviceId,
            error,
        });

        return false;
    }
}

async function restoreExistingManagedRecovery(cli: MatrixClient, recovery: StoredRecoveryRecord): Promise<void> {
    const cryptoApi = cli.getCrypto();

    const matrixUserId = cli.getSafeUserId();

    if (!cryptoApi || !recovery.recoveryKey) {
        throw new Error("Matrix crypto or active managed recovery key is unavailable");
    }

    const validated = await validateAndCacheRecoveryKey(cli, matrixUserId, recovery.recoveryKey);

    /*
     * The vault metadata must refer to the same Secret Storage
     * identity which the supplied key actually unlocks.
     */
    if (recovery.secretStorageKeyId && recovery.secretStorageKeyId !== validated.keyId) {
        throw new Error("The Simplii recovery vault record refers to a different Secret Storage key");
    }

    await bootstrapExistingCrossSigning(cli);

    await ensureCurrentDeviceCrossSigned(cli);

    const backupInfo = await cryptoApi.getKeyBackupInfo();

    if (!backupInfo) {
        /*
         * There is no previous backup to restore. Creating one helps
         * future devices, but cannot recreate already-lost room keys.
         */
        await cryptoApi.resetKeyBackup();

        const enabled = await cryptoApi.checkKeyBackupAndEnable();

        if (!enabled) {
            throw new Error("Matrix key backup could not be created");
        }

        console.info("[Simplii Chat] Created Matrix key backup for existing managed account", {
            matrixUserId,
            deviceId: cli.getDeviceId(),
        });

        return;
    }

    await restoreBackupFromSecretStorage(cli);

    console.info("[Simplii Chat] Existing managed Matrix recovery completed", {
        matrixUserId,
        deviceId: cli.getDeviceId(),
    });
}

function generatedKeyFromStoredRecoveryKey(recoveryKey: string): GeneratedSecretStorageKey {
    return {
        encodedPrivateKey: recoveryKey.trim(),

        privateKey: decodeManagedRecoveryKey(recoveryKey),
    };
}

async function initialiseNewAccountEncryption(
    cli: MatrixClient,
    generatedKey: GeneratedSecretStorageKey,
    replaceExisting = false,
): Promise<void> {
    const cryptoApi = cli.getCrypto();

    const matrixUserId = cli.getSafeUserId();

    const deviceId = cli.getDeviceId();

    if (!cryptoApi || !deviceId) {
        throw new Error("Matrix crypto or device ID is unavailable");
    }

    const recoveryKey = generatedKey.encodedPrivateKey?.trim();

    if (!recoveryKey) {
        throw new Error("Generated Matrix recovery key has no encoded private key");
    }

    /*
     * Store as pending before Matrix account changes.
     */
    console.info("[Simplii Chat] About to save pending managed recovery", {
        matrixUserId,
        deviceId,
    });

    const pendingSaved = await saveStoredRecoveryKey(matrixUserId, recoveryKey, {
        status: "pending",
        deviceId,
        replaceExisting,
    });

    if (!pendingSaved) {
        throw new Error("Brain did not confirm pending recovery-key storage");
    }

    await cryptoApi.bootstrapSecretStorage({
        setupNewSecretStorage: true,
        setupNewKeyBackup: false,

        createSecretStorageKey: async () => generatedKey,
    });

    const secretStorageKeyId = await refreshDefaultSecretStorageKeyId(cli, matrixUserId);

    if (!secretStorageKeyId) {
        throw new Error("Matrix Secret Storage was created without a default key ID");
    }

    const validated = await validateAndCacheRecoveryKey(cli, matrixUserId, recoveryKey);

    if (validated.keyId !== secretStorageKeyId) {
        throw new Error("Generated recovery key does not match the new default Secret Storage key");
    }

    await cryptoApi.bootstrapCrossSigning({
        setupNewCrossSigning: true,
        authUploadDeviceSigningKeys,
    });

    /*
     * Store private cross-signing keys in the newly-created
     * Secret Storage.
     */
    await cryptoApi.bootstrapSecretStorage({
        setupNewSecretStorage: false,
        setupNewKeyBackup: false,
    });

    /*
     * Create the first Matrix key backup.
     */
    await cryptoApi.resetKeyBackup();

    const backupEnabled = await cryptoApi.checkKeyBackupAndEnable();

    if (!backupEnabled) {
        throw new Error("New Matrix key backup could not be enabled");
    }

    const backupInfo = await cryptoApi.getKeyBackupInfo();

    const possibleVersion = (
        backupInfo as {
            version?: unknown;
        } | null
    )?.version;

    const keyBackupVersion = typeof possibleVersion === "string" ? possibleVersion : undefined;

    await ensureCurrentDeviceCrossSigned(cli);

    /*
     * Activate the vault record only after Matrix confirms that
     * this exact recovery key unlocks the account.
     */
    const activeSaved = await saveStoredRecoveryKey(matrixUserId, recoveryKey, {
        status: "active",
        secretStorageKeyId,
        keyBackupVersion,
        deviceId,
        replaceExisting,
    });

    if (!activeSaved) {
        throw new Error("Brain did not confirm activation of the validated recovery key");
    }

    console.info("[Simplii Chat] New managed Matrix encryption setup completed", {
        matrixUserId,
        deviceId,
        secretStorageKeyId,
        keyBackupVersion,
    });
}

async function resetStaleExistingAccount(cli: MatrixClient): Promise<void> {
    const cryptoApi = cli.getCrypto();

    const matrixUserId = cli.getSafeUserId();

    const deviceId = cli.getDeviceId();

    if (!cryptoApi || !deviceId) {
        throw new Error("Matrix crypto or device ID is unavailable for stale-account migration");
    }

    console.warn("[Simplii Chat] Starting stale managed-recovery migration", {
        matrixUserId,
        deviceId,
    });

    /*
     * Remove any cached value belonging to the previous recovery
     * configuration.
     */
    resetSimpliiManagedRecoveryState();

    /*
     * resetSimpliiManagedRecoveryState() clears matrixClientByUser.
     * Register the current client again because Secret Storage
     * callbacks will need it during the migration.
     */
    matrixClientByUser.set(matrixUserId, cli);

    /*
     * Destructive account-level reset:
     *
     * - deletes the existing key backup;
     * - removes the existing default Secret Storage configuration;
     * - resets cross-signing;
     * - creates a new backup.
     *
     * This cannot recover already-lost historical keys.
     */
    // await cryptoApi.resetEncryption(authUploadDeviceSigningKeys);
    console.info("[Simplii Chat] About to reset Matrix encryption", {
        matrixUserId,
        deviceId,
    });

    await cryptoApi.resetEncryption(authUploadDeviceSigningKeys);

    console.info("[Simplii Chat] Matrix encryption reset completed", {
        matrixUserId,
        deviceId,
    });

    /*
     * Generate the new account recovery key.
     */
    console.info("[Simplii Chat] Creating managed recovery key");

    const generatedKey = await cryptoApi.createRecoveryKeyFromPassphrase();

    console.info("[Simplii Chat] Managed recovery key created");

    const recoveryKey = generatedKey.encodedPrivateKey?.trim();

    if (!recoveryKey) {
        throw new Error("Matrix did not generate an encoded recovery key");
    }

    /*
     * Replace the stale database row with a pending setup.
     */
    const pendingSaved = await saveStoredRecoveryKey(matrixUserId, recoveryKey, {
        status: "pending",
        deviceId,
        replaceExisting: true,
    });

    if (!pendingSaved) {
        throw new Error("Brain did not confirm pending recovery-key storage");
    }

    /*
     * Create a new Secret Storage key using this exact generated
     * recovery key.
     *
     * resetEncryption() already created a new backup and new
     * cross-signing identity. bootstrapSecretStorage() stores the
     * locally-known private cross-signing and backup keys inside
     * the new Secret Storage.
     */
    await cryptoApi.bootstrapSecretStorage({
        setupNewSecretStorage: true,
        setupNewKeyBackup: false,

        createSecretStorageKey: async () => generatedKey,
    });

    const secretStorageKeyId = await refreshDefaultSecretStorageKeyId(cli, matrixUserId);

    if (!secretStorageKeyId) {
        throw new Error("The stale-account migration created no default Secret Storage key");
    }

    /*
     * Cryptographically confirm that the generated recovery key
     * opens the newly-created Secret Storage.
     */
    const validated = await validateAndCacheRecoveryKey(cli, matrixUserId, recoveryKey);

    if (validated.keyId !== secretStorageKeyId) {
        throw new Error("The generated recovery key does not match the new Secret Storage key");
    }

    /*
     * resetEncryption() creates the new cross-signing identity.
     * This call ensures it is ready and stored correctly.
     */
    await cryptoApi.bootstrapCrossSigning({
        setupNewCrossSigning: false,
        authUploadDeviceSigningKeys,
    });

    const crossSigningReady = await cryptoApi.isCrossSigningReady();

    if (!crossSigningReady) {
        throw new Error("The new Matrix cross-signing identity is not ready");
    }

    /*
     * Publish a self-signing signature for this current device.
     */
    await ensureCurrentDeviceCrossSigned(cli);

    /*
     * Verify that the backup created by resetEncryption() is
     * enabled and trusted.
     */
    const backupCheck = await cryptoApi.checkKeyBackupAndEnable();

    if (!backupCheck) {
        throw new Error("The new Matrix key backup could not be enabled");
    }

    let keyBackupVersion: string | undefined;

    try {
        const activeVersion = await cryptoApi.getActiveSessionBackupVersion();

        if (typeof activeVersion === "string" && activeVersion) {
            keyBackupVersion = activeVersion;
        }
    } catch {
        const backupInfo = await cryptoApi.getKeyBackupInfo();

        const version = (
            backupInfo as {
                version?: unknown;
            } | null
        )?.version;

        if (typeof version === "string") {
            keyBackupVersion = version;
        }
    }

    if (!keyBackupVersion) {
        throw new Error("The new Matrix key backup has no active version");
    }

    /*
     * Activate the vault record only after every Matrix-side check
     * has succeeded.
     */
    const activeSaved = await saveStoredRecoveryKey(matrixUserId, recoveryKey, {
        status: "active",
        secretStorageKeyId,
        keyBackupVersion,
        deviceId,
        replaceExisting: true,
    });

    if (!activeSaved) {
        throw new Error("Brain did not confirm activation of the migrated recovery key");
    }

    console.warn("[Simplii Chat] Stale managed-recovery migration completed", {
        matrixUserId,
        deviceId,
        secretStorageKeyId,
        keyBackupVersion,
    });
}

async function runManagedRecovery(cli: MatrixClient): Promise<void> {
    const matrixUserId = cli.getSafeUserId();

    /*
     * The Simplii recovery vault must always be checked first.
     *
     * Browser-local crypto must not hide a missing server-side
     * recovery record.
     */
    const accountState = await inspectAccountCryptoState(cli);

    const recovery = await getStoredRecoveryRecord(matrixUserId);

    console.info("[Simplii Chat] Managed recovery record received", {
        matrixUserId,
        status: recovery.status,

        hasRecoveryKey: Boolean(recovery.recoveryKey),

        secretStorageKeyId: recovery.secretStorageKeyId,

        keyBackupVersion: recovery.keyBackupVersion,
    });

    /*
     * Matrix already has encryption state.
     *
     * null:
     *   No Simplii recovery record exists yet. Since legacy
     *   history is intentionally not being preserved, establish
     *   a fresh managed encryption identity.
     *
     * stale/pending:
     *   Resume/recreate managed recovery.
     */
    if (accountState.hasAnyEncryptionSetup) {
        if (recovery.status === null || recovery.status === "stale" || recovery.status === "pending") {
            await resetStaleExistingAccount(cli);

            return;
        }

        if (!recovery.recoveryKey || recovery.status !== "active") {
            throw new Error("Existing Matrix encryption setup has no active Simplii recovery key");
        }

        /*
         * Local crypto may be used only after Brain has confirmed
         * that an ACTIVE managed recovery record exists.
         */
        if (await tryRecoverUsingLocalCrypto(cli)) {
            console.info("[Simplii Chat] Active managed recovery confirmed; local crypto is sufficient", {
                matrixUserId,
                deviceId: cli.getDeviceId(),
            });

            return;
        }

        await restoreExistingManagedRecovery(cli, recovery);

        return;
    }

    /*
     * Matrix has no encryption setup but Brain claims there is
     * already an active recovery record. Do not silently create
     * another Matrix encryption identity.
     */
    if (recovery.recoveryKey || recovery.status === "active") {
        throw new Error("Brain has an active recovery record, but Matrix has no matching encryption setup");
    }

    const cryptoApi = cli.getCrypto();

    if (!cryptoApi) {
        throw new Error("Matrix Rust crypto is unavailable");
    }

    /*
     * Genuinely new encryption account.
     */
    const generatedKey = await cryptoApi.createRecoveryKeyFromPassphrase();

    await initialiseNewAccountEncryption(
        cli,
        generatedKey,

        recovery.status === "stale" || recovery.status === "pending",
    );

    console.info("[Simplii Chat] Matrix managed recovery setup finished", {
        matrixUserId,
        deviceId: cli.getDeviceId(),
    });
}

export async function startSimpliiManagedRecovery(cli: MatrixClient): Promise<void> {
    if (!isBrainEmbedded()) {
        return;
    }

    const matrixUserId = cli.getUserId();

    const deviceId = cli.getDeviceId();

    if (!matrixUserId || !deviceId) {
        throw new Error("Matrix user ID or device ID is unavailable for managed recovery");
    }

    matrixClientByUser.set(matrixUserId, cli);

    const sessionId = recoverySessionId(matrixUserId, deviceId);

    if (completedRecoverySessions.has(sessionId)) {
        return;
    }

    const existingPromise = recoveryInFlight.get(sessionId);

    if (existingPromise) {
        await existingPromise;
        return;
    }

    const recoveryPromise = runManagedRecovery(cli);

    recoveryInFlight.set(sessionId, recoveryPromise);

    try {
        await recoveryPromise;

        completedRecoverySessions.add(sessionId);

        console.info("[Simplii Chat] Managed Matrix recovery finished successfully", {
            matrixUserId,
            deviceId,
        });
    } catch (error) {
        console.error("[Simplii Chat] Managed Matrix recovery failed", {
            matrixUserId,
            deviceId,
            error,
        });

        throw error;
    } finally {
        recoveryInFlight.delete(sessionId);
    }
}

export function resetSimpliiManagedRecoveryState(): void {
    encodedRecoveryKeyCache.clear();
    secretStorageKeyCache.clear();
    defaultSecretStorageKeyIdByUser.clear();
    matchedSecretStorageKeyIdByUser.clear();
    matrixClientByUser.clear();
    recoveryInFlight.clear();
    completedRecoverySessions.clear();
}
